import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Cloud, CloudOff, UploadCloud, RefreshCw, X, AlertTriangle, HardDrive } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useOffline } from "@/lib/offline/useOffline";
import { setManual, push, discard, download, refreshAll, refreshStale } from "@/lib/offline/engine";

const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

const Change = ({ e }) => (
  <li className="flex items-start gap-2 py-2" data-testid={`sync-change-${e.id}`}>
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm text-[#F3EBDD]">{e.label}</div>
      <div className="text-[11px] text-[#A8A090]">{ago(new Date(e.ts).toISOString())}</div>
      {e.error && <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[#F87171]" data-testid={`sync-change-error-${e.id}`}><AlertTriangle className="h-3 w-3" />{e.error}</div>}
    </div>
    <button onClick={() => discard([e.id])} title="Discard this change" className="rounded p-1 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F87171]" data-testid={`sync-discard-${e.id}`}><X className="h-3.5 w-3.5" /></button>
  </li>
);

const UniverseBlock = ({ uid, m, changes, busy, canNet }) => (
  <div className="rounded-md border border-[#2D3442] bg-[#13171F] p-3" data-testid={`sync-universe-${uid}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate font-display text-lg text-[#F3EBDD]">{m.name}</div>
        <div className="text-[11px] text-[#A8A090]">{busy ? `Downloading… ${busy.done}/${busy.total}` : `Copy from ${ago(m.at)}`}</div>
      </div>
      <Button size="sm" variant="ghost" disabled={!canNet || !!busy || changes.length > 0} title={changes.length ? "Push or discard changes first" : "Download the latest copy"}
        onClick={() => download(uid).then(() => toast.success("Offline copy refreshed")).catch(() => toast.error("Couldn't refresh the offline copy"))} data-testid={`sync-refresh-${uid}`}>
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
      </Button>
    </div>
    {changes.length > 0 ? <ul className="mt-2 divide-y divide-[#2D3442]">{changes.map((e) => <Change key={e.id} e={e} />)}</ul>
      : <p className="mt-2 text-xs text-[#A8A090]">No local changes.</p>}
    {m.stale && changes.length > 0 && <p className="mt-2 text-[11px] text-[#A8A090]" data-testid={`sync-stale-hint-${uid}`}>Discarded changes leave this device's copy once the rest are pushed or discarded.</p>}
  </div>
);

export const SyncPanel = () => {
  const s = useOffline();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  useQuery({ queryKey: ["billing"], queryFn: () => api.get("/billing/me").then((r) => r.data), enabled: !!user });
  useEffect(() => {
    const on = () => qc.invalidateQueries();
    const show = () => setOpen(true);
    window.addEventListener("lf-offline-mode", on);
    window.addEventListener("lf-open-sync", show);
    return () => { window.removeEventListener("lf-offline-mode", on); window.removeEventListener("lf-open-sync", show); };
  }, [qc]);
  const ids = Object.keys(s.meta);
  if (!s.user || (!ids.length && !s.outbox.length)) return null;
  const canNet = navigator.onLine && !s.auto;
  const n = s.outbox.length;
  const toggle = async (v) => {
    if (v && canNet && !n) { const t = toast.loading("Updating your offline copies…"); await refreshAll(); toast.dismiss(t); }
    if (!v) refreshStale();
    setManual(v);
    toast.success(v ? "Working offline. Changes stay on this device until you push them." : n ? `Online. This device's copy stays in use until you push ${n} change${n === 1 ? "" : "s"}.` : "Back online");
  };
  const doPush = async () => {
    const r = await push();
    if (r.stopped) toast.error("Lost the connection. Your remaining changes are still on this device.");
    else if (r.failed) toast.error(`${r.pushed} pushed, ${r.failed} couldn't be pushed. See the list.`);
    else toast.success(`${r.pushed} change${r.pushed === 1 ? "" : "s"} pushed to the cloud`);
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className={`fixed bottom-24 right-5 z-40 md:bottom-[4.5rem] md:right-6 flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium shadow-lg backdrop-blur-md transition-colors ${s.offline ? "border-[#F59E0B]/60 bg-[#1A1508]/90 text-[#FBBF24]" : "border-[#2D3442] bg-[#13171F]/90 text-[#E8DFD1] hover:border-[#D97706]/60"}`} data-testid="sync-pill">
        {s.offline ? <CloudOff className="h-3.5 w-3.5" /> : <Cloud className="h-3.5 w-3.5 text-[#10B981]" />}
        <span data-testid="sync-pill-mode">{s.offline ? "Offline" : n ? "Local · push needed" : "Online"}</span>
        {n > 0 && <span className="rounded-full bg-[#D97706] px-1.5 text-[10px] font-bold text-[#0B0D10]" data-testid="sync-pill-count">{n}</span>}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-full overflow-y-auto border-[#2D3442] bg-[#0B0D10] p-5 sm:max-w-md lf-scroll" data-testid="sync-panel">
          <SheetTitle className="font-display text-3xl text-[#F3EBDD]">Offline & sync</SheetTitle>
          <p className="mt-1 text-sm text-[#A8A090]">Universes saved on this computer. Edits made offline stay here until you push them.</p>
          <div className="mt-5 flex items-center justify-between rounded-md border border-[#2D3442] bg-[#13171F] p-3">
            <div><div className="flex items-center gap-1.5 text-sm text-[#F3EBDD]"><HardDrive className="h-4 w-4 text-[#F59E0B]" />Work offline</div>
              <div className="text-xs text-[#A8A090]">{!navigator.onLine ? "No internet connection right now" : s.auto ? "Can't reach LoreRight. Retrying in the background" : "Use the copies on this device, even when online"}</div></div>
            <Switch checked={s.manual} onCheckedChange={toggle} data-testid="sync-work-offline-toggle" />
          </div>
          <div className="mt-5 space-y-3">
            {ids.map((uid) => <UniverseBlock key={uid} uid={uid} m={s.meta[uid]} busy={s.busy[uid]} canNet={canNet} changes={s.outbox.filter((e) => e.uid === uid)} />)}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={doPush} disabled={!n || !canNet || s.pushing} data-testid="sync-push-button"><UploadCloud className="mr-1.5 h-4 w-4" />{s.pushing ? "Pushing…" : `Push to cloud${n ? ` (${n})` : ""}`}</Button>
            {n > 0 && <Button variant="ghost" onClick={() => { if (window.confirm("Discard every change that hasn't been pushed? This can't be undone.")) { discard(s.outbox.map((e) => e.id)); toast.success("Local changes discarded"); } }} className="text-[#F87171]" data-testid="sync-discard-all">Discard all</Button>}
          </div>
          {!canNet && n > 0 && <p className="mt-2 text-xs text-[#A8A090]">Connect to the internet to push.</p>}
          <p className="mt-6 text-xs text-[#A8A090]">If a note or chapter was also changed online, both versions are merged when you push. AI tools, live co-editing, comments, exports and publishing need the internet.</p>
        </SheetContent>
      </Sheet>
    </>
  );
};
